import { Trash2 } from 'lucide-react';
import { Button } from './ui/Button';
import { sortByDivisionName } from '../utils/sortDivisionNames';
import { highlightRowClass, isHighlightedClass, stickyCellBg } from '../utils/classHighlight';

export function buildDayHistoryRows(classes, historicalStrengths, dateIso) {
  const byClassId = new Map(
    (historicalStrengths || []).map((row) => [String(row.class_id), row])
  );

  const rows = (classes || []).map((cls) => {
    const record = byClassId.get(String(cls.id));
    const boys = parseInt(record?.boys, 10) || 0;
    const girls = parseInt(record?.girls, 10) || 0;
    return {
      class_id: cls.id,
      division_name: cls.division_name,
      id: record?.id ?? null,
      boys,
      girls,
      total: boys + girls,
      date: record?.date ?? dateIso,
      hasRecord: Boolean(record),
    };
  });

  return sortByDivisionName(rows);
}

export function DayHistoryTable({
  rows,
  labels,
  onDelete,
  deleteLoading,
  showDelete = true,
  highlightClassIds = [],
}) {
  if (!rows?.length) {
    return null;
  }

  const footerBoys = rows.reduce((sum, r) => sum + r.boys, 0);
  const footerGirls = rows.reduce((sum, r) => sum + r.girls, 0);
  const footerTotal = footerBoys + footerGirls;

  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full min-w-max text-sm">
        <thead>
          <tr className="border-b border-border bg-surface">
            <th className="sticky left-0 z-10 bg-surface px-3 py-2 text-left font-medium text-muted shadow-sticky">
              {labels.division}
            </th>
            <th className="border-l border-border px-3 py-2 text-center font-medium text-muted">
              {labels.boys}
            </th>
            <th className="px-3 py-2 text-center font-medium text-muted">{labels.girls}</th>
            <th className="border-l border-border px-3 py-2 text-center font-medium text-muted">
              {labels.totalStudents}
            </th>
            {showDelete && (
            <th className="border-l border-border px-3 py-2 text-center font-medium text-muted w-16">
              {labels.actions}
            </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const highlighted = isHighlightedClass(row.class_id, highlightClassIds);
            return (
            <tr
              key={row.class_id}
              className={`border-b border-border hover:bg-surface/50 ${highlightRowClass(highlighted)}`}
            >
              <td
                className={`sticky left-0 z-10 px-3 py-2 font-medium text-text shadow-sticky ${stickyCellBg(highlighted)}`}
              >
                {row.division_name}
              </td>
              <td className="border-l border-border px-3 py-2 text-center tabular-nums">
                {row.boys}
              </td>
              <td className="px-3 py-2 text-center tabular-nums">{row.girls}</td>
              <td className="border-l border-border px-3 py-2 text-center font-medium tabular-nums">
                {row.total}
              </td>
              {showDelete && (
              <td className="border-l border-border px-3 py-2 text-center">
                {row.hasRecord && row.id && onDelete ? (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => onDelete(row.id, row.date)}
                    isLoading={deleteLoading}
                    aria-label={labels.delete}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ) : (
                  <span className="text-xs text-muted">—</span>
                )}
              </td>
              )}
            </tr>
            );
          })}
          <tr className="bg-primary-light/40 font-semibold">
            <td className="sticky left-0 z-10 bg-primary-light/40 px-3 py-2 text-text shadow-sticky">
              {labels.grandTotal}
            </td>
            <td className="border-l border-border px-3 py-2 text-center tabular-nums">{footerBoys}</td>
            <td className="px-3 py-2 text-center tabular-nums">{footerGirls}</td>
            <td className="border-l border-border px-3 py-2 text-center tabular-nums">{footerTotal}</td>
            {showDelete && <td className="border-l border-border px-3 py-2" />}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function getConsolidateGroup(divisionName) {
  const name = String(divisionName ?? '').trim().toUpperCase();
  if (name.startsWith('LKG') || name.startsWith('UKG')) return 'KG';
  const gradeMatch = name.match(/^(\d{1,2})/);
  if (gradeMatch) return gradeMatch[1];
  return name || 'Other';
}

function compareConsolidateGroup(a, b) {
  if (a === 'KG' && b !== 'KG') return -1;
  if (b === 'KG' && a !== 'KG') return 1;
  const an = parseInt(a, 10);
  const bn = parseInt(b, 10);
  const aIsNum = !Number.isNaN(an);
  const bIsNum = !Number.isNaN(bn);
  if (aIsNum && bIsNum) return an - bn;
  if (aIsNum) return -1;
  if (bIsNum) return 1;
  return a.localeCompare(b);
}

export function buildDayConsolidatedRows(dayRows) {
  const map = new Map();
  (dayRows || []).forEach((row) => {
    const group = getConsolidateGroup(row.division_name);
    const prev = map.get(group) || { group, boys: 0, girls: 0, total: 0 };
    prev.boys += parseInt(row.boys, 10) || 0;
    prev.girls += parseInt(row.girls, 10) || 0;
    prev.total = prev.boys + prev.girls;
    map.set(group, prev);
  });

  return Array.from(map.values()).sort((a, b) => compareConsolidateGroup(a.group, b.group));
}

export function DayConsolidatedTable({ rows, labels, highlightGroups = [] }) {
  if (!rows?.length) return null;

  const highlightSet = new Set(highlightGroups);

  const footerBoys = rows.reduce((sum, r) => sum + r.boys, 0);
  const footerGirls = rows.reduce((sum, r) => sum + r.girls, 0);
  const footerTotal = footerBoys + footerGirls;

  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full min-w-max text-sm">
        <thead>
          <tr className="border-b border-border bg-surface">
            <th className="sticky left-0 z-10 bg-surface px-3 py-2 text-left font-medium text-muted shadow-sticky">
              {labels.classNumber}
            </th>
            <th className="border-l border-border px-3 py-2 text-center font-medium text-muted">
              {labels.boys}
            </th>
            <th className="px-3 py-2 text-center font-medium text-muted">{labels.girls}</th>
            <th className="border-l border-border px-3 py-2 text-center font-medium text-muted">
              {labels.totalStudents}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const highlighted = highlightSet.has(row.group);
            return (
            <tr
              key={row.group}
              className={`border-b border-border hover:bg-surface/50 ${highlightRowClass(highlighted)}`}
            >
              <td
                className={`sticky left-0 z-10 px-3 py-2 font-medium text-text shadow-sticky ${stickyCellBg(highlighted)}`}
              >
                {row.group}
              </td>
              <td className="border-l border-border px-3 py-2 text-center tabular-nums">{row.boys}</td>
              <td className="px-3 py-2 text-center tabular-nums">{row.girls}</td>
              <td className="border-l border-border px-3 py-2 text-center font-medium tabular-nums">
                {row.total}
              </td>
            </tr>
            );
          })}
          <tr className="bg-primary-light/40 font-semibold">
            <td className="sticky left-0 z-10 bg-primary-light/40 px-3 py-2 text-text shadow-sticky">
              {labels.grandTotal}
            </td>
            <td className="border-l border-border px-3 py-2 text-center tabular-nums">{footerBoys}</td>
            <td className="px-3 py-2 text-center tabular-nums">{footerGirls}</td>
            <td className="border-l border-border px-3 py-2 text-center tabular-nums">{footerTotal}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
